import Details from '@/components/Details/Details';
import { ROUTE_INVITATION_DETAILS } from '@/constants/routes';
import timezoneService from '@/services/timezone.service';
import { ICalendar } from '@/types/ICalendar';
import { ICalendarEvent } from '@/types/calendar-event/ICalendarEvent';
import {
	Box,
	Button,
	ButtonGroup,
	DrawerBody,
	DrawerFooter,
	Flex,
	FormControl,
	FormHelperText,
	FormLabel,
	Grid,
	IconButton,
	Image,
	Input,
	InputGroup,
	InputRightElement,
	Select,
	Text,
	Textarea,
	VStack,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { FiEye, FiTrash } from 'react-icons/fi';
import { TbUserCancel, TbUserCheck, TbUserQuestion } from 'react-icons/tb';
import { Link as RRLink } from 'react-router-dom';
import { useEventDetailsFormController } from './event-details-form.controller';

interface IProps {
	event: ICalendarEvent;
	calendar: ICalendar;
	onClose: () => void;
	onDelete: (event: ICalendarEvent) => void;
	onSave: (event: ICalendarEvent) => void;
}

export default function EventDetailsBody(props: IProps) {
	const {
		formParticipant,
		fields,
		commentsField,
		from,
		to,
		form,
		handleDeleteEventClick,
		handleFormParticipantSubmit,
		handleRemoveParticipantClick,
		handleSave,
		handleDeleteDateClick,
	} = useEventDetailsFormController(props);

	return (
		<>
			<DrawerBody overflow="auto">
				<Grid mt={4} gap={0.5}>
					<Details title="General" isOpen>
						<FormControl>
							<FormLabel>{t('Status')}</FormLabel>
							<Select {...form.register('status')}>
								<option value="CONFIRMED">{t('Event confirmed')}</option>
								<option value="TENTATIVE">{t('Event pending')}</option>
								<option value="CANCELLED">{t('Event cancelled')}</option>
							</Select>
						</FormControl>

						<FormControl mt={4}>
							<FormLabel>{t('Summary')}</FormLabel>
							<Input {...form.register('summary')} />
						</FormControl>

						<FormControl mt={4}>
							<FormLabel>{t('Description')}</FormLabel>
							<Input {...form.register('description')} />
						</FormControl>

						<VStack>
							<Grid w="full" my={4} templateColumns="1fr 1fr" gap={4}>
								<FormControl id="start">
									<FormLabel>{t('Start')}</FormLabel>
									<FormHelperText fontSize="x-small">{timezoneService.getTimeZone()}</FormHelperText>
									<Input
										onChange={(e) => {
											form.setValue('interval.inclusiveStart.dateTime', e.target.value);
										}}
										value={from}
										type="datetime-local"
									/>
								</FormControl>

								<FormControl id="end">
									<FormLabel>{t('End')}</FormLabel>
									<FormHelperText fontSize="x-small">{timezoneService.getTimeZone()}</FormHelperText>
									<Input
										placeholder={to}
										onChange={(e) => {
											form.setValue('interval.exclusiveEnd.dateTime', e.target.value);
										}}
										value={to}
										type="datetime-local"
									/>
								</FormControl>
							</Grid>
							{form.watch('interval') ? (
								<Button onClick={handleDeleteDateClick} w="full" size="xs" colorScheme="red" variant="outline">
									{t('Delete date')}
								</Button>
							) : null}
						</VStack>

						<Box mt={4}>
							<Text fontWeight={500}>{t('Notes')}</Text>
							{commentsField.fields.map((c, index) => {
								return (
									<InputGroup px={0} key={c.id}>
										<Input
											variant="flushed"
											key={c.id}
											type="text"
											value={form.watch(`comments.${index}`).value}
											onChange={(e) => form.setValue(`comments.${index}`, { value: e.target.value })}
										/>
										<InputRightElement>
											<IconButton
												onClick={() => {
													commentsField.remove(index);
												}}
												variant="ghost"
												size="xs"
												icon={<FiTrash />}
												aria-label={'Delete'}
											/>
										</InputRightElement>
									</InputGroup>
								);
							})}
							<Button
								mt={4}
								size="sm"
								w="100%"
								onClick={() =>
									commentsField.append({
										value: '',
									})
								}
							>
								{t('Add note')}
							</Button>
						</Box>
					</Details>

					<Details title={t('Tags')}>
						<Box>
							{Object.keys(props.event.tags).map((k: string) => {
								return <KeyValue key={k} k={k} v={props.event.tags[k]} />;
							})}
						</Box>
					</Details>

					<Details title={t('Media')}>
						{props.event.attachments.map((item) => {
							return (
								<Image
									_hover={{
										cursor: 'pointer',
										position: 'absolute',
										transform: 'scale(3)',
										zIndex: 99,
									}}
									transition="all .4s"
									key={item.url}
									borderRadius="sm"
									shadow="md"
									maxW={60}
									maxH={40}
									src={item.url}
									fallbackSrc="https://via.placeholder.com/150"
								/>
							);
						})}
					</Details>

					<Details title={t('Attendees')}>
						<Box px={4}>
							<FormControl>
								<FormLabel>{t('Add attendee')}</FormLabel>
								<InputGroup>
									<Input {...formParticipant.register('email', { required: true })} type="email"></Input>
									<InputRightElement w="auto">
										<Button
											isDisabled={!formParticipant.formState.isValid}
											borderLeftRadius={0}
											onClick={handleFormParticipantSubmit}
											type="submit"
											colorScheme="primary"
										>
											{t('Invite')}
										</Button>
									</InputRightElement>
								</InputGroup>
							</FormControl>

							<Box as="details" mt={4}>
								<Button size="sm" as="summary">
									{t('Toggle invitation options')}
								</Button>
								<Box mt={2} p={4} borderWidth={1} borderColor="gray.200" borderRadius="md">
									<FormControl>
										<FormLabel>{t('Summary')}</FormLabel>
										<Input {...formParticipant.register('summary')} type="text"></Input>
									</FormControl>

									<FormControl mt={2}>
										<FormLabel>{t('Subtitle')}</FormLabel>
										<Input {...formParticipant.register('subtitle')} type="text"></Input>
									</FormControl>

									<FormControl mt={2}>
										<FormLabel>{t('Invitation description')}</FormLabel>
										<Textarea {...formParticipant.register('description')}></Textarea>
									</FormControl>

									<FormControl mt={2}>
										<FormLabel>{t('Mode')}</FormLabel>
										<Select {...formParticipant.register('displayTimeMode')}>
											<option value="FUZZY">{t('Fuzzy')}</option>
											<option value="EXACT">{t('Exact')}</option>
											<option value="START_DATE">{t('Start date')}</option>
										</Select>
									</FormControl>
								</Box>
							</Box>
						</Box>

						<Box p={4} display="grid" gap={1}>
							<FormLabel>{t('Attendees')}</FormLabel>

							{fields.map((attendee, index) => {
								return (
									<Grid
										key={attendee.id}
										alignContent="center"
										alignItems="center"
										borderColor="gray.200"
										borderRadius="md"
										borderWidth={1}
										gap={8}
										gridTemplateColumns="1fr auto"
										gridTemplateRows="1fr"
										px={4}
										py={2}
									>
										<Grid gap={2}>
											<Grid gridTemplateColumns="auto 1fr" alignContent="start" gap={3} alignItems="center">
												<>
													{attendee.status === 'ACCEPTED' ? (
														<TbUserCheck color="var(--chakra-colors-green-600)" />
													) : attendee.status === 'DECLINED' ? (
														<TbUserCancel color="var(--chakra-colors-red-600)" />
													) : (
														<TbUserQuestion color="var(--chakra-colors-gray-600)" />
													)}
												</>
												<Text>{attendee.email}</Text>
											</Grid>
										</Grid>

										<ButtonGroup isAttached>
											{attendee.invitation ? (
												<IconButton
													as={RRLink}
													to={ROUTE_INVITATION_DETAILS.replace(':id', attendee.invitation.id)}
													variant="ghost"
													size="sm"
													aria-label="Delete"
													icon={<FiEye />}
												/>
											) : null}
											<IconButton
												variant="ghost"
												onClick={() => handleRemoveParticipantClick(index)}
												size="sm"
												aria-label="Delete"
												icon={<FiTrash />}
											/>
										</ButtonGroup>
									</Grid>
								);
							})}
						</Box>
					</Details>
				</Grid>
			</DrawerBody>

			<DrawerFooter>
				<Flex justify="end" gap={4}>
					<Button
						type="button"
						isLoading={form.formState.isSubmitting}
						colorScheme="primary"
						variant="outline"
						onClick={() => {
							if (window.confirm(t('Confirm this action.'))) {
								handleDeleteEventClick();
							}
						}}
					>
						{t('Delete')}
					</Button>

					<Button colorScheme="primary" onClick={handleSave} isLoading={form.formState.isSubmitting}>
						{t('Save')}
					</Button>
				</Flex>
			</DrawerFooter>
		</>
	);
}

function KeyValue(props: { k: string; v: string }) {
	return (
		<Box display="grid" gridTemplateColumns="auto 1fr">
			<Text textTransform="capitalize" fontSize="xs" fontWeight={600} mr={1}>
				{props.k}:
			</Text>
			<Text fontSize="xs">{props.v}</Text>
		</Box>
	);
}
